import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Heart,
  Shield,
  PawPrint,
  ArrowRight,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Quote,
} from "lucide-react";
import ScrollingBanner from "../components/ScrollingBanner";
import FacebookIcon from "../components/icon/FacebookIcon";
import InstagramIcon from "../components/icon/InstagramIcon";
import OurMissionSection from "../components/OurMissionSection";
import ImpactStatsSection from "../components/ImpactStatsSection";
import groseille from "../images/groseille.jpeg";
import rousette from "../images/rousette.jpeg";

gsap.registerPlugin(ScrollTrigger);

export default function SavoinimauxLandingPage() {
  const containerRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroActionsRef = useRef(null);
  const heroVisualRef = useRef(null);
  const sectionRefs = useRef([]);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const stories = useMemo(
    () => [
      {
        name: "Roucette",
        image: rousette,
        title: "Voir sans les yeux, aimer sans limite.",
        text: "Après des années au sein d’un centre équestre, Roucette a été prise en charge par l’association alors que sa santé se dégradait. Devenue aveugle suite à une lourde opération, elle a pourtant retrouvé une vie remplie d’amour dans sa famille d’accueil. Entourée, rassurée et profondément attachante, elle a vécu ses derniers moments dans la douceur, laissant derrière elle une empreinte inoubliable.",
      },
      {
        name: "Groseille & ses 7 chatons",
        image: groseille,
        title: "Sept chatons, tous sauvés.",
        text: "Minette errante et gestante, Groseille a été trappée puis mise en sécurité par nos bénévoles. Quelques jours plus tard, elle donnait naissance à 7 chatons au chaud, dans sa famille d’accueil. Aujourd’hui, Groseille est stérilisée, en sécurité, et ses petits ont tous trouvé une famille.",
      },
      {
        name: "Milo",
        image:
          "https://images.unsplash.com/photo-1574158622682-e40e69881006?auto=format&fit=crop&w=1400&q=80",
        title: "Milo découvre qu’un foyer peut aussi rimer avec sérénité.",
        text: "Très réservé à son arrivée, Milo observait tout à distance. Avec du temps, des repères et de l’attention, il s’est ouvert. Aujourd’hui, il recherche une famille calme, prête à lui laisser l’espace nécessaire pour continuer à s’épanouir à son rythme.",
      },
    ],
    []
  );

  const [storyIndex, setStoryIndex] = useState(0);

  const [contactForm, setContactForm] = useState({
    fullName: "",
    department: "",
    phone: "",
    email: "",
    message: "",
  });

  const [contactStatus, setContactStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const handleContactChange = (field, value) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    setContactStatus({
      loading: true,
      success: false,
      error: "",
    });

    try {
      if (
        !contactForm.fullName.trim() ||
        !contactForm.email.trim() ||
        !contactForm.message.trim()
      ) {
        throw new Error(
          "Merci de remplir au minimum le nom, l’e-mail et le message."
        );
      }

      const response = await fetch("https://formspree.io/f/xgorvzjl", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          nom: contactForm.fullName,
          departement: contactForm.department,
          telephone: contactForm.phone,
          email: contactForm.email,
          message: contactForm.message,
        }),
      });

      if (!response.ok) {
        throw new Error("L’envoi du message a échoué. Réessaie dans un instant.");
      }

      setContactStatus({
        loading: false,
        success: true,
        error: "",
      });

      setContactForm({
        fullName: "",
        department: "",
        phone: "",
        email: "",
        message: "",
      });
    } catch (error) {
      setContactStatus({
        loading: false,
        success: false,
        error: error.message || "Une erreur est survenue.",
      });
    }
  };

  const cards = [
    {
      title: "Protection",
      text: "Nous intervenons pour mettre à l’abri les animaux abandonnés, blessés ou en danger, en leur apportant des soins, un cadre rassurant et un suivi adapté à leur état.",
      icon: Shield,
    },
    {
      title: "Adoption",
      text: "Chaque adoption est préparée avec sérieux afin de créer une rencontre durable entre l’animal et sa future famille, dans le respect de ses besoins et de son histoire.",
      icon: Heart,
    },
    {
      title: "Engagement",
      text: "Bénévoles, familles d’accueil, donateurs et soutiens de l’association forment un réseau essentiel pour offrir à chaque animal une vraie seconde chance.",
      icon: PawPrint,
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStoryIndex((prev) => (prev + 1) % stories.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [stories.length]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        heroTitleRef.current,
        { opacity: 0, y: isMobile ? 20 : 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
      );

      gsap.fromTo(
        heroTextRef.current,
        { opacity: 0, y: isMobile ? 14 : 28 },
        { opacity: 1, y: 0, duration: 0.7, delay: 0.12, ease: "power3.out" }
      );

      gsap.fromTo(
        heroActionsRef.current,
        { opacity: 0, y: isMobile ? 10 : 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 0.2, ease: "power3.out" }
      );

      gsap.fromTo(
        heroVisualRef.current,
        { opacity: 0, scale: isMobile ? 0.98 : 0.94, y: isMobile ? 10 : 20 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.85,
          delay: 0.1,
          ease: "power3.out",
        }
      );

      if (!isMobile) {
        sectionRefs.current.forEach((section) => {
          if (!section) return;
          gsap.fromTo(
            section,
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: section,
                start: "top 78%",
              },
            }
          );
        });
      }
    }, containerRef);

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      ctx.revert();
    };
  }, [isMobile]);

  const nextStory = () => {
    setStoryIndex((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    setStoryIndex((prev) => (prev - 1 + stories.length) % stories.length);
  };

  const currentStory = stories[storyIndex];

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen overflow-x-hidden bg-[#fbfbf9] text-[#111111]"
    >
      <div className="absolute inset-0 -z-30 bg-[linear-gradient(180deg,#fffdfb_0%,#fcfbf8_38%,#f8f6f2_100%)]" />

      <div className="absolute inset-0 -z-20 overflow-hidden pointer-events-none">
        <div className="absolute left-[-120px] top-[-120px] h-[420px] w-[420px] rounded-full bg-red-200/30 blur-[120px]" />
        <div className="absolute right-[-120px] top-[18%] h-[380px] w-[380px] rounded-full bg-red-100/40 blur-[120px]" />
        <div className="absolute bottom-[-140px] left-[10%] h-[420px] w-[420px] rounded-full bg-orange-100/30 blur-[130px]" />
        <div className="absolute bottom-[-120px] right-[-80px] h-[360px] w-[360px] rounded-full bg-black/[0.04] blur-[120px]" />

        {!isMobile && (
          <>
            <div className="absolute left-[8%] top-[28%] h-[220px] w-[220px] rounded-full border border-black/[0.04] bg-white/30 backdrop-blur-3xl" />
            <div className="absolute right-[12%] top-[62%] h-[180px] w-[180px] rounded-full border border-red-200/30 bg-red-50/30 backdrop-blur-3xl" />
          </>
        )}
      </div>

      {!isMobile && (
        <>
          <div className="absolute inset-0 -z-10 pointer-events-none opacity-[0.045] [background-image:linear-gradient(to_right,black_1px,transparent_1px),linear-gradient(to_bottom,black_1px,transparent_1px)] [background-size:72px_72px]" />
          <div className="absolute inset-0 -z-[5] pointer-events-none opacity-[0.035] [background-image:radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.22)_1px,transparent_0)] [background-size:26px_26px]" />
        </>
      )}

      <main>
        <section className="relative">
          <div className="mx-auto grid min-h-[88vh] max-w-7xl items-center gap-10 px-6 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-20">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-[11px] uppercase tracking-[0.32em] text-red-600">
                <span className="h-2 w-2 rounded-full bg-red-600" />
                Association de protection animale
              </div>

              <h1
                ref={heroTitleRef}
                className="mt-6 max-w-4xl text-5xl font-semibold leading-[0.95] text-[#111111] md:text-7xl xl:text-7xl"
              >
                Ils n&apos;ont rien demandé.
                <span className="block text-red-600">
                  Mais ils ont tout perdu.
                </span>
              </h1>

              <p
                ref={heroTextRef}
                className="mt-8 max-w-2xl text-base leading-8 text-black/65 md:text-lg"
              >
                Savoinimaux protège, soigne et accompagne les animaux abandonnés,
                perdus ou en danger. Cette version du site garde une base claire
                et lumineuse, tout en ajoutant une vraie dimension premium,
                éditoriale et émotionnelle.
              </p>

              <div
                ref={heroActionsRef}
                className="mt-10 flex flex-col gap-4 sm:flex-row"
              >
                <a
                  href="#engagement"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_15px_50px_rgba(220,38,38,0.18)] transition hover:translate-y-[-2px] hover:bg-red-500"
                >
                  Faire un don
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#adoption"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-7 py-4 text-sm font-semibold text-[#111111] transition hover:border-red-200 hover:bg-red-50"
                >
                  Suivre nos animaux sur les réseaux
                </a>
              </div>
            </div>

            <div ref={heroVisualRef} className="relative order-1 lg:order-2">
              {!isMobile && (
                <>
                  <div className="absolute -left-10 top-10 hidden h-40 w-40 rounded-full bg-red-100 blur-3xl xl:block" />
                  <div className="absolute -right-6 bottom-12 hidden h-52 w-52 rounded-full bg-black/5 blur-3xl xl:block" />
                  <div className="absolute left-[-18px] top-20 hidden text-red-100 xl:block">
                    <PawPrint className="h-20 w-20 rotate-[-14deg]" />
                  </div>
                  <div className="absolute right-[-10px] top-12 hidden text-black/[0.05] xl:block">
                    <Sparkles className="h-14 w-14" />
                  </div>
                </>
              )}

              <div className="group relative overflow-hidden rounded-[2.2rem] border border-black/6 bg-white shadow-[0_18px_40px_rgba(0,0,0,0.08)] md:shadow-[0_30px_100px_rgba(0,0,0,0.08)]">
                <img
                  src="https://images.unsplash.com/photo-1518717758536-85ae29035b6d?auto=format&fit=crop&w=1400&q=80"
                  alt="Chien dans une ambiance cinématique"
                  className="h-[560px] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 via-transparent to-transparent" />

                <div className="absolute left-6 top-6 rounded-full border border-white/20 bg-white/80 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-[#111111] backdrop-blur-md">
                  Sauver. Soigner. Protéger.
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="max-w-lg rounded-[1.8rem] border border-white/20 bg-white/80 p-6 backdrop-blur-xl">
                    <p className="text-xs uppercase tracking-[0.34em] text-black/40">
                      Une mission concrète
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold text-[#111111] md:text-3xl">
                      Chaque animal mérite une seconde chance.
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-black/60">
                      Derrière chaque prise en charge, il y a une histoire, une
                      urgence et un engagement humain. Savoinimaux agit chaque
                      jour pour protéger, soigner et accompagner des animaux
                      abandonnés ou en difficulté, avec l’objectif de leur offrir
                      un avenir plus stable et plus serein.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div id="mission" ref={(el) => (sectionRefs.current[0] = el)}>
          <OurMissionSection />
        </div>

        <ImpactStatsSection />

        <section
          id="impact"
          ref={(el) => (sectionRefs.current[1] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr]">
              <div className="relative overflow-hidden rounded-[2.2rem] border border-black/6 bg-white p-3 shadow-[0_20px_50px_rgba(0,0,0,0.05)] md:shadow-[0_26px_80px_rgba(0,0,0,0.05)]">
                <img
                  src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1400&q=80"
                  alt="Main en contact avec une patte"
                  className="h-full min-h-[420px] w-full rounded-[1.7rem] object-cover"
                />
                <div className="absolute inset-3 rounded-[1.7rem] bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 max-w-md">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/72">
                    Notre engagement
                  </p>
                  <h3 className="mt-3 text-3xl font-semibold text-white">
                    Des gestes concrets, chaque semaine.
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-white/80">
                    Soins, accueils, accompagnements, sensibilisation : derrière
                    chaque chiffre, il y a des histoires, du temps, et un
                    véritable engagement humain.
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center rounded-[2.2rem] border border-black/6 bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.04)] md:p-10 md:shadow-[0_24px_70px_rgba(0,0,0,0.04)]">
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Pourquoi agir
                </p>

                <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#111111] md:text-5xl">
                  Une réalité locale,
                  <span className="block text-red-600">
                    et des besoins concrets.
                  </span>
                </h2>

                <p className="mt-6 max-w-2xl text-base leading-8 text-black/62">
                  Chaque année, de nombreux animaux sont abandonnés, perdus ou
                  laissés sans soins. Derrière ces situations, il y a souvent
                  l’urgence, l’isolement ou un manque de moyens. Savoinimaux
                  intervient pour apporter des solutions concrètes, rapidement et
                  efficacement.
                </p>

                <p className="mt-4 max-w-2xl text-base leading-8 text-black/62">
                  L’association agit en lien avec des familles d’accueil, des
                  vétérinaires et des partenaires locaux afin d’assurer une prise
                  en charge complète : du sauvetage jusqu’à l’adoption. Chaque
                  intervention est pensée pour offrir une solution durable à
                  l’animal.
                </p>

                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                  {[
                    "Interventions locales en Savoie",
                    "Suivi des animaux sur le long terme",
                    "Accompagnement des adoptants",
                    "Mobilisation d’un réseau solidaire",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-black/6 bg-[#fafafa] px-5 py-4 text-sm text-black/66"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="partners"
          ref={(el) => (sectionRefs.current[2] = el)}
          className="relative py-10"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mb-6 flex items-end justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Ils soutiennent l’association
                </p>
                <h2 className="mt-3 text-3xl font-semibold text-[#111111] md:text-4xl">
                  Un réseau de partenaires engagés aux côtés de Savoinimaux
                </h2>
              </div>
              <a
                href="/partenaires"
                className="hidden text-sm font-semibold text-red-600 transition hover:text-red-500 md:inline-flex"
              >
                Voir la page partenaires
              </a>
            </div>

            <div className="relative overflow-hidden rounded-[1.8rem] border border-black/6 bg-white py-5 shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />

              <div className="flex w-max animate-[marquee_28s_linear_infinite] gap-4 px-6 [@keyframes_marquee]{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}">
                {[
                  "Clinique vétérinaire partenaire",
                  "Refuge local",
                  "Entreprise mécène",
                  "Collectif bénévole",
                  "Commerce solidaire",
                  "Partenaire événementiel",
                  "Clinique vétérinaire partenaire",
                  "Refuge local",
                  "Entreprise mécène",
                  "Collectif bénévole",
                  "Commerce solidaire",
                  "Partenaire événementiel",
                ].map((partner, index) => (
                  <div
                    key={`${partner}-${index}`}
                    className="flex min-w-[220px] items-center justify-center rounded-full border border-black/8 bg-[#fafafa] px-6 py-4 text-sm font-medium text-[#111111]"
                  >
                    {partner}
                  </div>
                ))}
              </div>
            </div>

            <a
              href="/partenaires"
              className="mt-6 inline-flex text-sm font-semibold text-red-600 transition hover:text-red-500 md:hidden"
            >
              Voir la page partenaires
            </a>
          </div>
        </section>

        <section
          id="stories"
          ref={(el) => (sectionRefs.current[3] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Histoires coup de cœur
                </p>
                <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
                  Ils ont connu l’abandon,
                  <span className="block text-red-600">
                    ils découvrent une seconde chance.
                  </span>
                </h2>

                <p className="mt-5 text-base leading-8 text-black/62">
                  Chaque animal pris en charge a une histoire. Derrière chaque
                  regard, il y a un passé, souvent difficile, et un chemin vers
                  une nouvelle vie. Ces histoires témoignent du travail réalisé
                  par l’association et de l’impact concret de chaque
                  intervention.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={prevStory}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] shadow-sm transition hover:border-red-200 hover:bg-red-50"
                  aria-label="Histoire précédente"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={nextStory}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] shadow-sm transition hover:border-red-200 hover:bg-red-50"
                  aria-label="Histoire suivante"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="overflow-hidden rounded-[2.2rem] border border-black/6 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.05)] md:shadow-[0_26px_80px_rgba(0,0,0,0.05)]">
                <img
                  key={currentStory.image}
                  src={currentStory.image}
                  alt={currentStory.name}
                  className="h-96 w-full object-cover md:h-[470px]"
                />
              </div>

              <div className="flex flex-col justify-center rounded-[2.2rem] border border-black/6 bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.04)] md:p-10 md:shadow-[0_26px_80px_rgba(0,0,0,0.04)]">
                <div className="flex items-center gap-3 text-red-600">
                  <Quote className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-[0.34em]">
                    Histoire {storyIndex + 1} / {stories.length}
                  </span>
                </div>

                <h3 className="mt-5 text-4xl font-semibold text-[#111111] md:text-5xl">
                  {currentStory.name}
                </h3>
                <p className="mt-4 text-xl leading-9 text-[#1c1c1c]">
                  {currentStory.title}
                </p>
                <p className="mt-6 text-base leading-8 text-black/62">
                  {currentStory.text}
                </p>

                <div className="mt-10 flex items-center gap-3">
                  {stories.map((story, index) => (
                    <button
                      key={story.name}
                      onClick={() => setStoryIndex(index)}
                      className={`h-2.5 rounded-full transition ${
                        index === storyIndex ? "w-10 bg-red-600" : "w-2.5 bg-black/12"
                      }`}
                      aria-label={`Afficher l'histoire ${story.name}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="adoption"
          ref={(el) => (sectionRefs.current[4] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mx-auto mb-7 max-w-4xl text-center">
              <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                Animaux à adopter
              </p>
              <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
                Les animaux sont présentés sur
                <span className="block text-red-600">
                  Facebook et Instagram.
                </span>
              </h2>
              <p className="mt-6 text-base leading-8 text-black/62">
                Les profils d’adoption ne sont pas publiés directement sur le
                site. Savoinimaux met en avant ses animaux à adopter
                principalement via ses réseaux sociaux, afin de partager plus
                facilement les publications, les urgences et les nouvelles
                arrivées.
              </p>
            </div>

            <ScrollingBanner />

            <div className="mt-14 grid gap-6 md:grid-cols-2">
              <div className="rounded-[2rem] border border-black/6 bg-white p-8 shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5f5f5] text-[#111111]">
                    <FacebookIcon className="h-10 w-10 text-[#1877F2]" />
                  </div>
                  <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
                    Mises à jour régulières
                  </span>
                </div>
                <h3 className="mt-6 text-3xl font-semibold text-[#111111]">
                  Facebook
                </h3>
                <p className="mt-4 text-base leading-8 text-black/60">
                  Retrouvez les publications d’adoption, les sauvetages récents,
                  les appels urgents et les partages de la communauté sur la
                  page Facebook de l’association.
                </p>
                <a
                  href="https://www.facebook.com/Savoinimaux/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-500"
                >
                  Voir les animaux a adopter
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="rounded-[2rem] border border-black/6 bg-white p-8 shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5f5f5] text-[#111111]">
                    <InstagramIcon className="h-10 w-10 text-pink-600" />
                  </div>
                  <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
                    Mises à jour régulières
                  </span>
                </div>
                <h3 className="mt-6 text-3xl font-semibold text-[#111111]">
                  Instagram
                </h3>
                <p className="mt-4 text-base leading-8 text-black/60">
                  Suivez les histoires du quotidien, les nouveaux animaux pris en
                  charge, les adoptions réussies et les coulisses de
                  l’association à travers un contenu plus vivant et plus
                  spontané.
                </p>
                <a
                  href="https://www.instagram.com/savoinimaux/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-500"
                >
                  Voir les animaux a adopter
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          id="engagement"
          ref={(el) => (sectionRefs.current[5] = el)}
          className="relative py-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="relative overflow-hidden rounded-[2.5rem] border border-red-100 bg-[linear-gradient(135deg,rgba(220,38,38,0.08),rgba(255,255,255,0.92))] p-8 shadow-[0_20px_50px_rgba(220,38,38,0.10)] md:p-12 md:shadow-[0_30px_100px_rgba(220,38,38,0.10)]">
              <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-red-100 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-40 w-40 rounded-full bg-black/5 blur-3xl" />
              {!isMobile && (
                <div className="absolute right-8 top-8 hidden text-red-100 lg:block">
                  <PawPrint className="h-16 w-16 rotate-[18deg]" />
                </div>
              )}

              <div className="relative z-10 grid gap-10 lg:grid-cols-[1fr_0.72fr]">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-black/45">
                    Comment aider
                  </p>
                  <h2 className="mt-4 max-w-3xl text-4xl font-semibold leading-tight text-[#111111] md:text-6xl">
                    Ensemble, changeons leur destin.
                  </h2>
                  <p className="mt-6 max-w-2xl text-base leading-8 text-black/62">
                    Soutenir Savoinimaux, ce n’est pas seulement faire un don.
                    C’est aussi permettre à l’association de continuer ses prises
                    en charge, ses suivis vétérinaires, ses accueils d’urgence et
                    son travail quotidien auprès d’animaux fragilisés, abandonnés
                    ou en attente d’un nouveau départ.
                  </p>
                  <p className="mt-4 max-w-2xl text-base leading-8 text-black/62">
                    Chaque participation compte. Un partage sur les réseaux peut
                    aider à trouver une famille. Une famille d’accueil peut offrir
                    un sas indispensable à un animal en reconstruction. Un
                    bénévole peut faire gagner un temps précieux sur le terrain.
                    Et un don peut déclencher une intervention au bon moment.
                  </p>
                  <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                    <a
                      href="#"
                      className="inline-flex items-center justify-center rounded-full bg-red-600 px-7 py-4 text-sm font-semibold text-white transition hover:bg-red-500"
                    >
                      Faire un don
                    </a>
                    <a
                      href="#contact-form"
                      className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white px-7 py-4 text-sm font-semibold text-[#111111] transition hover:bg-red-50"
                    >
                      Nous contacter
                    </a>
                  </div>
                </div>

                <div className="grid gap-4 self-end">
                  {[
                    {
                      title: "Faire un don",
                      text: "Contribuez directement aux soins vétérinaires, à l’alimentation, au matériel et aux interventions d’urgence.",
                    },
                    {
                      title: "Devenir famille d’accueil",
                      text: "Offrez un environnement temporaire à un animal en attente d’adoption et participez activement à son équilibre.",
                    },
                    {
                      title: "Rejoindre les bénévoles",
                      text: "Transports, aide logistique, présence sur les évènements, communication ou suivi terrain : chacun peut trouver sa place.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="rounded-[1.6rem] border border-black/6 bg-white p-5 shadow-[0_12px_40px_rgba(0,0,0,0.04)]"
                    >
                      <p className="text-lg font-semibold text-[#111111]">
                        {item.title}
                      </p>
                      <p className="mt-2 text-sm leading-7 text-black/60">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="contact-form"
          ref={(el) => (sectionRefs.current[6] = el)}
          className="relative pb-24"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                Contact
              </p>

              <h2 className="mt-4 text-4xl font-semibold text-[#111111] md:text-6xl">
                Échanger avec
                <span className="block text-red-600">l’association</span>
              </h2>

              <p className="mt-6 text-base leading-8 text-black/62">
                Une question, un signalement ou une envie d’aider ? Contactez-nous
                simplement via ce formulaire.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="flex flex-col justify-between rounded-[2.2rem] border border-black/6 bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.04)] md:p-10 md:shadow-[0_22px_70px_rgba(0,0,0,0.04)]">
                <div>
                  <h3 className="text-2xl font-semibold text-[#111111]">
                    Association Savoinimaux
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-black/60">
                    47 Rue du Pont Neuf, 74150 Rumilly, France
                    <br />
                    Boutique solidaire : 6 Rue Filaterie - 74150 Rumilly
                  </p>

                  <p className="mt-4 text-sm leading-7 text-black/60">
                    <span className="font-medium text-black">Mail :</span>{" "}
                    savoinimaux@gmail.com
                    <br />
                    <span className="font-medium text-black">Tel :</span> 06 44 68
                    52 53
                  </p>

                  <p className="mt-6 text-sm font-medium text-red-600">
                    En cas d’urgence, contactez un vétérinaire de garde.
                  </p>
                </div>

                <div className="mt-8 flex gap-4">
                  <a
                    href="https://www.facebook.com/Savoinimaux/"
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:bg-red-50 hover:text-red-600"
                  >
                    <FacebookIcon className="h-5 w-5" />
                  </a>

                  <a
                    href="https://www.instagram.com/savoinimaux/"
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:bg-red-50 hover:text-red-600"
                  >
                    <InstagramIcon className="h-5 w-5" />
                  </a>
                </div>

                <div className="mt-10 overflow-hidden rounded-xl border border-black/10">
                  <iframe
                    title="Carte Savoinimaux"
                    src="https://www.google.com/maps?q=47%20Rue%20du%20Pont%20Neuf,%2074150%20Rumilly,%20France&z=14&output=embed"
                    className="h-[220px] w-full border-0"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="rounded-[2.2rem] border border-black/6 bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.04)] md:p-10 md:shadow-[0_22px_70px_rgba(0,0,0,0.04)]">
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                    Formulaire
                  </p>

                  <h3 className="mt-3 text-2xl font-semibold text-[#111111] md:text-3xl">
                    Nous contacter
                  </h3>

                  <p className="mt-3 text-sm text-black/60">
                    Réponse généralement sous 24 à 48h.
                  </p>
                </div>

                <form className="space-y-5" onSubmit={handleContactSubmit}>
                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                      Nom / Prénom
                    </label>
                    <input
                      type="text"
                      placeholder="Jean Dupont"
                      value={contactForm.fullName}
                      onChange={(e) =>
                        handleContactChange("fullName", e.target.value)
                      }
                      className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        Département
                      </label>
                      <select
                        value={contactForm.department}
                        onChange={(e) =>
                          handleContactChange("department", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      >
                        <option value="">Sélectionner</option>
                        <option value="Savoie">Savoie</option>
                        <option value="Haute-Savoie">Haute-Savoie</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        placeholder="06 00 00 00 00"
                        value={contactForm.phone}
                        onChange={(e) =>
                          handleContactChange("phone", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                        E-mail
                      </label>
                      <input
                        type="email"
                        placeholder="email@email.com"
                        value={contactForm.email}
                        onChange={(e) =>
                          handleContactChange("email", e.target.value)
                        }
                        className="w-full rounded-2xl border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-wide text-black/50">
                      Message
                    </label>
                    <textarea
                      rows={6}
                      placeholder="Décrivez votre demande..."
                      value={contactForm.message}
                      onChange={(e) =>
                        handleContactChange("message", e.target.value)
                      }
                      className="w-full rounded-[1.6rem] border border-black/10 bg-[#fafafa] px-4 py-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
                    />
                  </div>

                  {contactStatus.error && (
                    <p className="text-sm font-medium text-red-600">
                      {contactStatus.error}
                    </p>
                  )}

                  {contactStatus.success && (
                    <p className="text-sm font-medium text-green-600">
                      Votre message a bien été envoyé.
                    </p>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={contactStatus.loading}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-red-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_12px_40px_rgba(220,38,38,0.25)] transition hover:bg-red-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    >
                      {contactStatus.loading
                        ? "Envoi en cours..."
                        : "Envoyer le message"}
                      {!contactStatus.loading && (
                        <span className="transition group-hover:translate-x-1">
                          →
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}